import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { PageHeader, ActionBtn, Card, PeriodPicker, EL_MONTH_NAMES } from '../components/Shared';
import toast from 'react-hot-toast';
import styles from './Pages.module.css';

export default function AnalyticsPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heatmap, setHeatmap] = useState(null);
  const [heatmapDays, setHeatmapDays] = useState(90);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  // @period-picker-v1 — period-scoped figures. Defaults to the current
  // month, which is what the endpoint assumes when the params are absent.
  const _now = new Date();
  const [period, setPeriod] = useState({ year: _now.getFullYear(), month: _now.getMonth() + 1 });

  useEffect(() => {
    setLoading(true);
    analyticsAPI.getReport({ year: period.year, month: period.month })
      .then(r => setReport(r.data.data))
      .catch(() => toast.error('Αδυναμία φόρτωσης'))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    setHeatmapLoading(true);
    analyticsAPI.getBookingHeatmap(heatmapDays)
      .then(r => setHeatmap(r.data.data))
      .catch(() => toast.error('Αδυναμία φόρτωσης heatmap'))
      .finally(() => setHeatmapLoading(false));
  }, [heatmapDays]);

  if (loading) return <div className={styles.page}><div className={styles.loading}>Φόρτωση...</div></div>;

  const r = report || {};
  const ratings = r.ratingsOverview || {};

  const StatCard = ({ icon, label, value, sub, color }) => (
    <div style={{ background: 'white', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 52, height: 52, borderRadius: 12, background: color || 'var(--crimson-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy)' }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );

  const RatingBar = ({ label, value, max = 5, color = 'var(--crimson)' }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: 'var(--slate)' }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>{value}/{max}</span>
      </div>
      <div style={{ height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(value / max) * 100}%`, background: color, borderRadius: 4 }} />
      </div>
    </div>
  );

  const NpsGauge = ({ score }) => {
    const getColor = (s) => s >= 50 ? 'var(--success)' : s >= 0 ? 'var(--warning)' : 'var(--danger)';
    const getLabel = (s) => s >= 50 ? 'Εξαιρετικό' : s >= 0 ? 'Καλό' : 'Χρειάζεται βελτίωση';
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 56, fontWeight: 700, color: getColor(score) }}>{score}</div>
        <div style={{ fontSize: 14, color: 'var(--muted)', marginTop: 4 }}>NPS Score</div>
        <div style={{ fontSize: 13, color: getColor(score), fontWeight: 600, marginTop: 8 }}>{getLabel(score)}</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 16 }}>
          <div><span style={{ fontSize: 18, fontWeight: 700, color: 'var(--success)' }}>{ratings.promoters || 0}</span><div style={{ fontSize: 11, color: 'var(--muted)' }}>Promoters</div></div>
          <div><span style={{ fontSize: 18, fontWeight: 700, color: 'var(--warning)' }}>{ratings.passives || 0}</span><div style={{ fontSize: 11, color: 'var(--muted)' }}>Passives</div></div>
          <div><span style={{ fontSize: 18, fontWeight: 700, color: 'var(--danger)' }}>{ratings.detractors || 0}</span><div style={{ fontSize: 11, color: 'var(--muted)' }}>Detractors</div></div>
        </div>
      </div>
    );
  };

  const SimpleBar = ({ data, color = 'var(--crimson)' }) => {
    const max = Math.max(...data.map(d => d.value), 1);
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120, padding: '10px 0' }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>{d.value}</div>
            <div style={{ width: '100%', height: `${(d.value / max) * 80}px`, background: d.color || color, borderRadius: 4, minHeight: 4 }} />
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6, textAlign: 'center' }}>{d.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const HeatMap = ({ matrix, total, description }) => {
    const dayLabels = ['Κυρ', 'Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ'];
    // find max value across all cells for colour scaling
    let maxVal = 0;
    for (const row of (matrix || [])) for (const v of row) if (v > maxVal) maxVal = v;
    const cellColor = (v) => {
      if (!v) return '#F8F9FB';
      const intensity = maxVal > 0 ? v / maxVal : 0;
      // scale from very light magenta to full magenta
      const alpha = 0.15 + intensity * 0.85;
      return `rgba(192, 24, 90, ${alpha})`;
    };
    const cellTextColor = (v) => {
      if (!v) return 'transparent';
      const intensity = maxVal > 0 ? v / maxVal : 0;
      return intensity > 0.5 ? '#fff' : 'var(--navy)';
    };
    return (
      <div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{description}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: 10, fontFamily: 'monospace' }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                {Array.from({ length: 24 }, (_, h) => (
                  <th key={h} style={{ minWidth: 22, textAlign: 'center', color: 'var(--muted)', fontWeight: 500, padding: '2px 0' }}>
                    {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(matrix || []).map((row, dow) => (
                <tr key={dow}>
                  <td style={{ color: 'var(--muted)', fontWeight: 600, paddingRight: 6, textAlign: 'right', fontSize: 11 }}>{dayLabels[dow]}</td>
                  {row.map((v, h) => (
                    <td key={h} title={`${dayLabels[dow]} ${String(h).padStart(2, '0')}:00 — ${v}`} style={{
                      minWidth: 22,
                      height: 22,
                      textAlign: 'center',
                      background: cellColor(v),
                      color: cellTextColor(v),
                      borderRadius: 3,
                      fontWeight: 600,
                    }}>
                      {v || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)', textAlign: 'right' }}>
          Σύνολο: <strong style={{ color: 'var(--navy)' }}>{total || 0}</strong>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Αναλυτικά & Αναφορές"
        subtitle="Επίδοση πλατφόρμας και αξιολογήσεις"
        action={
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <PeriodPicker
              year={period.year}
              month={period.month}
              onChange={(year, month) => setPeriod({ year, month })}
            />
            <ActionBtn onClick={() => toast('Εξαγωγή PDF...')}>⬇ Εξαγωγή</ActionBtn>
          </div>
        }
      />
      {/* Which figures move with the period and which do not — otherwise
          the all-time cards look broken when you change month. */}
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -8, marginBottom: 16 }}>
        Περίοδος: {EL_MONTH_NAMES[period.month - 1]} {period.year}. Τα «Συνολικές
        Διαβουλεύσεις», «Συνολικοί Ασθενείς» και οι πίνακες αξιολογήσεων
        αφορούν όλο το διάστημα λειτουργίας.
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard icon="🩺" label="Συνολικές Διαβουλεύσεις" value={r.totalConsultations || 0} sub={`${r.monthConsultations || 0} αυτό τον μήνα`} />
        <StatCard icon="👥" label="Συνολικοί Ασθενείς" value={r.totalPatients || 0} sub={`+${r.newPatientsMonth || 0} νέοι`} color="rgba(13,154,106,0.1)" />
        <StatCard icon="👨‍⚕️" label="Ενεργοί Ιατροί" value={r.activeDoctors || 0} color="rgba(30,58,95,0.1)" />
        <StatCard icon="💰" label="Έσοδα Μήνα" value={`€${(r.monthRevenue || 0).toFixed(2)}`} sub={`Σύνολο: €${(r.totalRevenue || 0).toFixed(2)}`} color="rgba(224,123,42,0.1)" />
      </div>

      {/* Ratings Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
        <Card title="📊 NPS Score">
          <NpsGauge score={ratings.npsScore || 0} />
        </Card>
        <Card title="⭐ Αξιολογήσεις">
          <div style={{ padding: '10px 0' }}>
            <RatingBar label="Ικανοποίηση από Ιατρό" value={ratings.avgExpertRating || 0} />
            <RatingBar label="Ευκολία Χρήσης App" value={ratings.avgAppEase || 0} color="var(--success)" />
            <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Επιλύθηκε το πρόβλημα;</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--success)' }}>{ratings.resolvedPercent || 0}%</span>
            </div>
          </div>
        </Card>
        <Card title="📝 Πρόσφατα Σχόλια">
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {(r.recentRatings || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)', fontSize: 13 }}>Δεν υπάρχουν σχόλια</div>
            ) : (
              (r.recentRatings || []).map((rating, i) => (
                <div key={i} style={{ padding: '12px 0', borderBottom: i < (r.recentRatings || []).length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>{rating.doctorName}</span>
                    <span style={{ fontSize: 11, color: 'var(--warning)' }}>{'★'.repeat(rating.expertRating)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--slate)', lineHeight: 1.4 }}>{rating.comment}</div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
        <Card title="Διαβουλεύσεις ανά Ιατρό">
          <SimpleBar data={(r.consultationsByDoctor || []).slice(0, 6).map(d => ({ label: d.lastName || 'N/A', value: d.count || 0 }))} />
        </Card>
        <Card title="Ακυρώσεις">
          <SimpleBar data={[
            { label: 'Ασθενής (νωρίς)', value: r.patientCancelEarly || 0, color: 'var(--warning)' },
            { label: 'Ασθενής (αργά)', value: r.patientCancelLate || 0, color: 'var(--danger)' },
            { label: 'Ιατρός', value: r.doctorCancel || 0, color: 'var(--navy)' },
          ]} />
        </Card>
        <Card title="Νέοι Ασθενείς (6 μήνες)">
          <SimpleBar data={(r.registrations || []).map(m => ({ label: m.monthName, value: m.count }))} color="var(--success)" />
        </Card>
      </div>

      {/* Doctor Ratings Table */}
      <Card title="🏆 Αξιολογήσεις Ιατρών">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Ιατρός</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Αξιολογήσεις</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Μ.Ο. Βαθμολογία</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>NPS</th>
              </tr>
            </thead>
            <tbody>
              {(r.doctorRatings || []).length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>Δεν υπάρχουν δεδομένα</td></tr>
              ) : (
                (r.doctorRatings || []).map((doc, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: ['#C0185A', '#0D9A6A', '#1E3A5F', '#E07B2A', '#7C3AED'][i % 5], display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 12 }}>{(doc.firstName?.[0] || '') + (doc.lastName?.[0] || '')}</div>
                        <span style={{ fontWeight: 600, color: 'var(--navy)' }}>Δρ. {doc.firstName} {doc.lastName}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', padding: '14px 16px' }}>{doc.ratingCount}</td>
                    <td style={{ textAlign: 'center', padding: '14px 16px' }}>
                      <span style={{ color: 'var(--warning)', marginRight: 4 }}>★</span>
                      <span style={{ fontWeight: 600 }}>{doc.avgRating}</span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '14px 16px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: doc.avgNps >= 8 ? 'var(--success-light)' : doc.avgNps >= 6 ? 'rgba(224,123,42,0.1)' : 'rgba(220,38,38,0.1)', color: doc.avgNps >= 8 ? 'var(--success)' : doc.avgNps >= 6 ? 'var(--warning)' : 'var(--danger)' }}>{doc.avgNps}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Utilization Table */}
      <div style={{ marginTop: 20 }}>
        <Card title="📅 Αξιοποίηση Ιατρών">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Ιατρός</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Σύνολο Slots</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Κλεισμένα</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Ελεύθερα</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Αξιοποίηση</th>
                </tr>
              </thead>
              <tbody>
                {(r.utilization || []).length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>Δεν υπάρχουν δεδομένα</td></tr>
                ) : (
                  (r.utilization || []).map((doc, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: ['#C0185A', '#0D9A6A', '#1E3A5F', '#E07B2A', '#7C3AED'][i % 5], display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 12 }}>{(doc.firstName?.[0] || '') + (doc.lastName?.[0] || '')}</div>
                          <span style={{ fontWeight: 600, color: 'var(--navy)' }}>Δρ. {doc.firstName} {doc.lastName}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', padding: '14px 16px' }}>{doc.totalSlots}</td>
                      <td style={{ textAlign: 'center', padding: '14px 16px' }}>{doc.bookedSlots}</td>
                      <td style={{ textAlign: 'center', padding: '14px 16px' }}>{doc.availableSlots}</td>
                      <td style={{ textAlign: 'center', padding: '14px 16px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: doc.utilizationRate > 70 ? 'var(--success-light)' : 'rgba(224,123,42,0.1)', color: doc.utilizationRate > 70 ? 'var(--success)' : 'var(--warning)' }}>{doc.utilizationRate}%</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* BOOKING HEATMAP — @analytics-heatmap-v1 */}
      <div style={{ marginTop: 20 }}>
        <Card title="🔥 Ώρες αιχμής">
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Περίοδος:</span>
            {[7, 30, 90, 365].map(d => (
              <button
                key={d}
                onClick={() => setHeatmapDays(d)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: heatmapDays === d ? 'var(--crimson)' : 'white',
                  color: heatmapDays === d ? 'white' : 'var(--slate)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {d === 365 ? '1 έτος' : `${d} ημέρες`}
              </button>
            ))}
            {heatmapLoading && <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}>Φόρτωση...</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>
                Πότε κλείνουν ραντεβού
              </h4>
              <HeatMap
                matrix={heatmap?.by_created?.matrix}
                total={heatmap?.by_created?.total}
                description="Ώρα που ο ασθενής πάτησε 'επιβεβαίωση'"
              />
            </div>
            <div>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>
                Πότε θέλουν να δούν ιατρό
              </h4>
              <HeatMap
                matrix={heatmap?.by_scheduled?.matrix}
                total={heatmap?.by_scheduled?.total}
                description="Ώρα του ίδιου του ραντεβού"
              />
            </div>
          </div>

          <div style={{ marginTop: 16, padding: 12, background: 'var(--bg)', borderRadius: 8, fontSize: 12, color: 'var(--muted)' }}>
            ℹ️ Ώρα Ελλάδας. Οι επίδειξη λογαριασμοί (demo) έχουν εξαιρεθεί.
          </div>
        </Card>
      </div>
    </div>
  );
}
